import { useState } from 'react';
import type { ReactNode } from 'react';

export interface AsyncModalHook<TResult> {
  show: () => Promise<TResult>;
  render: (
    renderFn: (returnFromModal: (result: TResult) => void) => ReactNode,
  ) => ReactNode;
}

interface Resolver<TResult> {
  resolve: (value: TResult) => void;
}

/**
 * Render modal-like components and get result in a super comfy async way
 *
 * Keep simple workflow in e.g. one click handler, no need to split modal result handling
 * to separate callback.
 *
 * Note: checked in Chrome that even when component is unmounted without resolving the promise,
 * references to the promise and async calls waiting for it are removed if there are
 * no other references to them, not causing a memory leak.
 *
 * @example
 * const dogNameModal = useAsyncModal<string | null>();
 *
 * return (
 *   <>
 *     <Button
 *       onClick={() => {
 *         const newDogName = await dogNameModal.show();
 *         if (newDogName !== null) {
 *           dispatch(renameDog(newDogName));
 *         }
 *       }}
 *     >
 *       Rename dog
 *     </Button>
 *     {dogNameModal.render(returnFromModal => (
 *       <DogNameDialog
 *         onClose={() => returnFromModal(null)}
 *         onConfirm={dogName => returnFromModal(dogName)}
 *       />
 *     ))}
 *   </>
 * );
 */
export const useAsyncModal = <TResult>(): AsyncModalHook<TResult> => {
  // resolve must be wrapped in an object because setState(function) triggers reducer-like behavior instead
  const [resolver, setResolver] = useState<Resolver<TResult> | null>(null);
  return {
    show: () =>
      new Promise((resolve) => {
        setResolver({ resolve });
      }),
    render: (renderFn) =>
      resolver === null
        ? null
        : renderFn((value) => {
            setResolver(null);
            resolver.resolve(value);
          }),
  };
};
